/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect } from 'react';
import { Stack } from '@mui/material';
import { useSessionStorage } from 'usehooks-ts';
import { ScoringContext } from '@/contexts/scoring/context';
import { useScoringRate } from '@/service/hooks/useScoringRate';
import { ComparisonRating } from '@/components/ComparisonRating/ComparisonRating';
import { COMMON_STRING } from '@/constants/dictionary';
import {
  StyledMenuItem,
  StyledMenuList,
} from '@/components/common/StyledMenuItems';
import { FIXTURE_TYPE_ID, USER_ROLE_NAME } from '@/service/constants';
import { UIStateContext } from '@/contexts/UIState/context';
import { PermissionsChecker } from '../PermissionsChecker/PermissionsChecker';
import { ActionsTable } from '../ActionsTable/ActionsTable';
import { SlaBreachFixtureEvaluation } from '../SlaBreachFixtureEvaluation/SlaBreachFixtureEvaluation';
import { SupervisorEvaluation } from '../SupervisorEvaluation/SupervisorEvaluation';
import { EvaluationSources } from '../EvaluationSources/EvaluationSources';

export const SS_QA_SUBTAB_KEY = 'MT-qa-subtabs-minder';

export enum QaSubPage {
  DataCollectorsEvaluation,
  SupervisorEvaluation,
  SlaBreachFixtureEvaluation,
  SlaBreachActionEvaluation,
  EvaluationSources,
}

export const FixtureComparison = () => {
  const { setIsSlaBreachActionSelected } = useContext(UIStateContext);

  const {
    state: { fixtureId, currentSport },
    fixtureConfigState: { fixtureConfig },
  } = useContext(ScoringContext);
  const { comparisonRating, isLoading } = useScoringRate();

  const comparisonRatingFilter =
    comparisonRating?.filter(
      (rating) => rating.role.name === USER_ROLE_NAME.MAIN_SCORER,
    ) || [];

  const supervisorAssigned = comparisonRating?.find(
    (rating) => rating.role.name === USER_ROLE_NAME.SUPERVISOR,
  );

  const isMatchOfficial =
    fixtureConfig?.fixture.type === FIXTURE_TYPE_ID.OFFICIAL;
  const isMatchSimulated =
    fixtureConfig?.fixture.type === FIXTURE_TYPE_ID.SIMULATION;
  const isSlaBreachFixtureAvailable =
    !!currentSport?.slaBreachQuantitySupported &&
    (isMatchOfficial || isMatchSimulated);
  const isSlaBreachActionAvailable =
    (isMatchSimulated || isMatchOfficial) &&
    !!currentSport?.slaBreachLatencySupported;
  const isDataCollectorsEvaluatinsAvailible =
    isMatchOfficial && !!comparisonRatingFilter?.length;

  const availableSubTabs = () => {
    const subTabs: QaSubPage[] = [QaSubPage.EvaluationSources];
    if (comparisonRating && isDataCollectorsEvaluatinsAvailible) {
      subTabs.push(QaSubPage.DataCollectorsEvaluation);
    }
    if (supervisorAssigned) {
      subTabs.push(QaSubPage.SupervisorEvaluation);
    }
    if (isSlaBreachFixtureAvailable) {
      subTabs.push(QaSubPage.SlaBreachFixtureEvaluation);
    }
    if (isSlaBreachActionAvailable) {
      subTabs.push(QaSubPage.SlaBreachActionEvaluation);
    }
    return subTabs;
  };

  const [storedSubTab, setStoredSubTab] = useSessionStorage<
    QaSubPage | undefined
  >(SS_QA_SUBTAB_KEY, QaSubPage.DataCollectorsEvaluation);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const availableTab = availableSubTabs();

    if (storedSubTab === undefined || !availableTab.includes(storedSubTab)) {
      setStoredSubTab(availableTab[0]);
    }
  }, [
    isLoading,
    comparisonRatingFilter,
    fixtureId,
    isSlaBreachActionAvailable,
    isSlaBreachFixtureAvailable,
  ]);

  useEffect(() => {
    if (storedSubTab !== undefined) {
      setIsSlaBreachActionSelected(
        storedSubTab === QaSubPage.SlaBreachActionEvaluation,
      );
    }
  }, [storedSubTab]);

  const isSubPageSelected = (subPage: QaSubPage) => subPage === storedSubTab;

  return (
    <PermissionsChecker name='FixturePage.QA.ExternalStats'>
      <Stack direction='row' flex='1' overflow='hidden'>
        <StyledMenuList>
          {isDataCollectorsEvaluatinsAvailible && (
            <PermissionsChecker name='FixturePage.QA.DataCollectorsEvaluation'>
              <StyledMenuItem
                selected={isSubPageSelected(QaSubPage.DataCollectorsEvaluation)}
                onClick={() => {
                  setStoredSubTab(QaSubPage.DataCollectorsEvaluation);
                }}
              >
                {COMMON_STRING.DATA_COLLECTORS_EVALUATION}
              </StyledMenuItem>
            </PermissionsChecker>
          )}
          {supervisorAssigned && (
            <PermissionsChecker name='FixturePage.QA.SupervisorEvaluation'>
              <StyledMenuItem
                selected={isSubPageSelected(QaSubPage.SupervisorEvaluation)}
                onClick={() => {
                  setStoredSubTab(QaSubPage.SupervisorEvaluation);
                }}
              >
                {COMMON_STRING.SUPERVISOR_EVALUATION}
              </StyledMenuItem>
            </PermissionsChecker>
          )}
          {isSlaBreachFixtureAvailable && (
            <PermissionsChecker name='FixturePage.QA.SlaBreachFixtureEvaluation'>
              <StyledMenuItem
                selected={isSubPageSelected(
                  QaSubPage.SlaBreachFixtureEvaluation,
                )}
                onClick={() => {
                  setStoredSubTab(QaSubPage.SlaBreachFixtureEvaluation);
                }}
              >
                {COMMON_STRING.SLA_BREACH_FIXTURE_EVALUATION}
              </StyledMenuItem>
            </PermissionsChecker>
          )}
          {isSlaBreachActionAvailable && (
            <PermissionsChecker name='FixturePage.QA.SlaBreachActionEvaluation'>
              <StyledMenuItem
                selected={isSubPageSelected(
                  QaSubPage.SlaBreachActionEvaluation,
                )}
                onClick={() => {
                  setStoredSubTab(QaSubPage.SlaBreachActionEvaluation);
                }}
              >
                {COMMON_STRING.SLA_BREACH_ACTION_EVALUATION}
              </StyledMenuItem>
            </PermissionsChecker>
          )}

          <PermissionsChecker name='FixturePage.QA.EvaluationSources'>
            <StyledMenuItem
              selected={isSubPageSelected(QaSubPage.EvaluationSources)}
              onClick={() => {
                setStoredSubTab(QaSubPage.EvaluationSources);
              }}
            >
              {COMMON_STRING.EVALUATION_SOURCES}
            </StyledMenuItem>
          </PermissionsChecker>
        </StyledMenuList>
        <Stack width={'100%'} overflow={'auto'}>
          {isSubPageSelected(QaSubPage.DataCollectorsEvaluation) &&
            !!comparisonRatingFilter?.length && (
              <PermissionsChecker name='FixturePage.QA.DataCollectorsEvaluation'>
                <ComparisonRating comparisonRating={comparisonRatingFilter} />
              </PermissionsChecker>
            )}
          {isSubPageSelected(QaSubPage.SupervisorEvaluation) &&
            supervisorAssigned && (
              <PermissionsChecker name='FixturePage.QA.SupervisorEvaluation'>
                <SupervisorEvaluation supervisor={supervisorAssigned} />
              </PermissionsChecker>
            )}
          {isSubPageSelected(QaSubPage.SlaBreachFixtureEvaluation) && (
            <PermissionsChecker name='FixturePage.QA.SlaBreachFixtureEvaluation'>
              <SlaBreachFixtureEvaluation />
            </PermissionsChecker>
          )}
          {isSubPageSelected(QaSubPage.SlaBreachActionEvaluation) && (
            <PermissionsChecker name='FixturePage.QA.SlaBreachActionEvaluation'>
              <ActionsTable />
            </PermissionsChecker>
          )}
          {isSubPageSelected(QaSubPage.EvaluationSources) && (
            <PermissionsChecker name='FixturePage.QA.EvaluationSources'>
              <EvaluationSources />
            </PermissionsChecker>
          )}
        </Stack>
      </Stack>
    </PermissionsChecker>
  );
};
